/**
 * usePhilipsDeviceConnector
 * -------------------------
 * Integrates Philips Device Connector (PDC) for SpeechMike button events in
 * Event Mode. Uses the same command dispatch as F-key shortcuts so behaviour
 * stays identical whether the device is in keyboard mode or Event Mode + PDC.
 *
 * Requires on the client:
 *  - Philips Device Connector Chrome extension
 *  - Philips Device Connector native host application
 *  - `<meta name="SPSPDCSDK" content="Enabled">` in index.html
 *
 * Silently no-ops when the extension is not installed (`typeof PDC` is undefined).
 *
 * @param {import("./philipsSpeechMikeActions").PhilipsSpeechMikeCommandOptions} options
 */
import { useEffect, useRef } from "react";

import { dispatchPhilipsSpeechMikeCommand } from "./philipsSpeechMikeActions";
import { pdcCommandsForNewlyPressedButtons } from "./philipsPdcButtonIds";
import {
  isPhilipsPdcExtensionAvailable,
  setPhilipsPdcConnectionStatus,
  syncPhilipsPdcDevicePresence,
  updatePhilipsPdcState,
} from "./philipsPdcConnectionState";

/** @returns {typeof PDC | undefined} */
function getPdcConstructor() {
  if (typeof window === "undefined") return undefined;
  return window.PDC;
}

export default function usePhilipsDeviceConnector(options) {
  const propsRef = useRef(options);
  const micToggleInFlightRef = useRef(false);
  const buttonMaskRef = useRef(0);
  const pdcRef = useRef(null);

  useEffect(() => {
    propsRef.current = options;
  });

  useEffect(() => {
    const PdcConstructor = getPdcConstructor();
    if (!PdcConstructor) {
      setPhilipsPdcConnectionStatus("absent");
      return undefined;
    }

    setPhilipsPdcConnectionStatus("extension_only");

    const runMicToggle = async (action) => {
      if (micToggleInFlightRef.current) return;
      micToggleInFlightRef.current = true;
      try {
        await action();
      } finally {
        micToggleInFlightRef.current = false;
      }
    };

    const handleButtonEvent = async (_deviceId, eventMask) => {
      const previousMask = buttonMaskRef.current;
      buttonMaskRef.current = eventMask;

      const commands = pdcCommandsForNewlyPressedButtons(eventMask, previousMask);
      if (!commands.length) return;

      const props = propsRef.current;
      for (const command of commands) {
        await dispatchPhilipsSpeechMikeCommand(command, props, runMicToggle);
      }
    };

    const pdc = new PdcConstructor();
    pdcRef.current = pdc;

    let connectedDeviceCount = 0;
    let primaryDeviceType = null;

    const refreshDevicePresence = () => {
      syncPhilipsPdcDevicePresence(connectedDeviceCount, primaryDeviceType);
    };

    pdc.onDeviceConnected = (_deviceId, deviceTypeName) => {
      connectedDeviceCount += 1;
      primaryDeviceType = deviceTypeName || primaryDeviceType;
      refreshDevicePresence();
    };

    pdc.onDeviceDisconnected = () => {
      connectedDeviceCount = Math.max(0, connectedDeviceCount - 1);
      if (connectedDeviceCount === 0) {
        primaryDeviceType = null;
      }
      refreshDevicePresence();
    };

    pdc.onButtonEvent = (deviceId, eventId) => {
      void handleButtonEvent(deviceId, eventId);
    };

    pdc.onNativeMessagingHostDisconnected = () => {
      buttonMaskRef.current = 0;
      connectedDeviceCount = 0;
      primaryDeviceType = null;
      if (isPhilipsPdcExtensionAvailable()) {
        updatePhilipsPdcState({
          status: "extension_only",
          deviceCount: 0,
          deviceTypeName: null,
        });
      }
    };

    pdc.connect().then(
      () => {
        updatePhilipsPdcState({
          status: "connected",
          deviceCount: 0,
          deviceTypeName: null,
        });
        pdc.getDeviceList?.().then(
          (deviceListResponse) => {
            const list = deviceListResponse?.deviceList ?? [];
            connectedDeviceCount = list.length;
            primaryDeviceType = list[0]?.deviceTypeName ?? null;
            refreshDevicePresence();
          },
          () => {
            refreshDevicePresence();
          },
        );
      },
      () => {
        updatePhilipsPdcState({
          status: "extension_only",
          deviceCount: 0,
          deviceTypeName: null,
        });
      },
    );

    return () => {
      buttonMaskRef.current = 0;
      pdcRef.current = null;
      if (isPhilipsPdcExtensionAvailable()) {
        updatePhilipsPdcState({
          status: "extension_only",
          deviceCount: 0,
          deviceTypeName: null,
        });
      } else {
        updatePhilipsPdcState({
          status: "absent",
          deviceCount: 0,
          deviceTypeName: null,
        });
      }
      try {
        pdc.disconnect();
      } catch {
        // ignore teardown errors
      }
    };
  }, []);
}
